/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  ColumnDef,
} from "@tanstack/react-table"
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@/components/ui/table"
import { useEffect, useMemo } from "react"
import { TableHeaderComponent } from "@/components/custom/Table/TableHeader"
import { EmptyTableRow } from "@/components/custom/Table/EmptyTableRow"
import { FieldRow } from "@/types/workflowHistory"
import { transformInputOutputToTableFriendlyRows } from "../WorkflowRunDetails/helpers/tableHelpers"
import { TablePagination } from "./TablePagination"

const MAX_TABLE_ROWS = 7
const MAX_TABLE_COLUMNS = 7

export const InputOutputTable = ({ data }: { data: unknown }) => {

  const flatData = useMemo(() => transformInputOutputToTableFriendlyRows(data as FieldRow[]), [data])

  const formatHeader = (key: string) => {
    return key
      .split(/[._]/) // split by dot or underscore
      .filter(segment => !/^\d+$/.test(segment)) // ignore segments that are only numbers
      .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1)) // capitalize
      .join(' ') // join with space
  }

  const columns = useMemo<ColumnDef<any>[]>(() => {
    if (!flatData.length) return []
    const allKeys = Array.from(
      new Set(flatData.flatMap(row => Object.keys(row)))
    ).slice(0, MAX_TABLE_COLUMNS)
    return allKeys.map((key) => ({
      accessorFn: (row) => row[key],
      id: key,
      header: formatHeader(key),
      cell: (info) => info.getValue(),
    }))
  }, [flatData])

  const table = useReactTable({
    data: flatData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: { pageSize: MAX_TABLE_ROWS },
    },
  })

  useEffect(() => {
    table.setPageSize(MAX_TABLE_ROWS)
  }, [table])

  return (
    <div>
      <div className="rounded-md border w-full">
        <Table className="w-full table-fixed">
          <TableHeaderComponent data={flatData} columns={columns} />
          <TableBody className="text-primary">
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="bg-foreground hover:bg-border/5 hover:cursor-auto"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className="max-w-[150px] truncate whitespace-nowrap overflow-hidden text-ellipsis py-2"
                      title={String(cell.getValue() ?? "")}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <EmptyTableRow numberOfColumns={columns.length} />
            )}
          </TableBody>
        </Table>
      </div>
      <TablePagination table={table} />
    </div>
  )
}
